import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { APP_CONSTANTS } from './constants';
import { Header } from './components/ui';
import { tokens } from './styles/tokens';

function SportsPage() {
  const navigate = useNavigate();
  const { 
    isAuthenticated, 
    loading, 
    logout, 
    userInfo
  } = useAuth();

  const [mlbScheduleInfo, setMlbScheduleInfo] = useState({
    text: 'Live games available',
    color: tokens.colors.accent[400],
    isToday: true
  });

  const [eplScheduleInfo, setEplScheduleInfo] = useState({
    text: 'Live games available',
    color: tokens.colors.accent[400],
    isToday: true
  });

  const [nbaScheduleInfo, setNbaScheduleInfo] = useState({
    text: 'Live games available',
    color: tokens.colors.accent[400],
    isToday: true
  });

  // Redirect if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, loading, navigate]);

  // Fetch games to determine scheduling status for all sports
  useEffect(() => {
    const fetchMLBSchedule = async () => {
      try {
        const response = await fetch(`${APP_CONSTANTS.ODDS_API_BASE_URL}/sports/baseball_mlb/odds/?apiKey=${APP_CONSTANTS.ODDS_API_KEY}&regions=us&markets=h2h`);
        const games = await response.json();
        
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const twoDaysFromNow = new Date(today.getTime() + 2 * 24 * 60 * 60 * 1000);
        
        // Check if there are any games in the next 2 days
        const gamesNext2Days = games.filter(game => {
          const gameTime = new Date(game.commence_time);
          return gameTime >= now && gameTime < twoDaysFromNow;
        });
        
        if (gamesNext2Days.length > 0) {
          const gamesToday = gamesNext2Days.filter(game => {
            const gameTime = new Date(game.commence_time);
            const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
            return gameTime >= today && gameTime < tomorrow;
          });
          
          if (gamesToday.length > 0) {
            setMlbScheduleInfo({
              text: `${gamesToday.length} games today`,
              color: tokens.colors.accent[400],
              isToday: true
            });
          } else {
            setMlbScheduleInfo({
              text: `${gamesNext2Days.length} games next 2 days`,
              color: tokens.colors.accent[400],
              isToday: true
            });
          }
        } else {
          // Get next 10 upcoming games
          const upcomingGames = games
            .filter(game => new Date(game.commence_time) >= now)
            .sort((a, b) => new Date(a.commence_time) - new Date(b.commence_time))
            .slice(0, 10);
          
          if (upcomingGames.length > 0) {
            const nextGameDate = new Date(upcomingGames[0].commence_time);
            const nextDateString = nextGameDate.toLocaleDateString('en-US', { 
              weekday: 'short', 
              month: 'short', 
              day: 'numeric' 
            });
            
            setMlbScheduleInfo({
              text: `${upcomingGames.length} upcoming games (next: ${nextDateString})`,
              color: tokens.colors.warning[400],
              isToday: false
            });
          } else {
            setMlbScheduleInfo({
              text: 'No games scheduled',
              color: tokens.colors.neutral[500],
              isToday: false
            });
          }
        }
      } catch (error) {
        console.error('Error fetching MLB schedule:', error);
        setMlbScheduleInfo({
          text: 'Schedule unavailable',
          color: tokens.colors.neutral[500],
          isToday: false
        });
      }
    };

    const fetchEPLSchedule = async () => {
      try {
        const response = await fetch(`${APP_CONSTANTS.ODDS_API_BASE_URL}/sports/soccer_epl/odds/?apiKey=${APP_CONSTANTS.ODDS_API_KEY}&regions=us&markets=h2h`);
        const games = await response.json();
        
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const twoDaysFromNow = new Date(today.getTime() + 2 * 24 * 60 * 60 * 1000);
        
        // Check if there are any games in the next 2 days
        const gamesNext2Days = games.filter(game => {
          const gameTime = new Date(game.commence_time);
          return gameTime >= now && gameTime < twoDaysFromNow;
        });
        
        if (gamesNext2Days.length > 0) {
          const gamesToday = gamesNext2Days.filter(game => {
            const gameTime = new Date(game.commence_time);
            const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
            return gameTime >= today && gameTime < tomorrow;
          });
          
          if (gamesToday.length > 0) {
            setEplScheduleInfo({
              text: `${gamesToday.length} games today`,
              color: tokens.colors.accent[400],
              isToday: true
            });
          } else {
            setEplScheduleInfo({
              text: `${gamesNext2Days.length} games next 2 days`,
              color: tokens.colors.accent[400],
              isToday: true
            });
          }
        } else {
          // Get next 10 upcoming games
          const upcomingGames = games
            .filter(game => new Date(game.commence_time) >= now)
            .sort((a, b) => new Date(a.commence_time) - new Date(b.commence_time))
            .slice(0, 10);
          
          if (upcomingGames.length > 0) {
            const nextGameDate = new Date(upcomingGames[0].commence_time);
            const nextDateString = nextGameDate.toLocaleDateString('en-US', { 
              weekday: 'short', 
              month: 'short', 
              day: 'numeric' 
            });
            
            setEplScheduleInfo({
              text: `${upcomingGames.length} upcoming games (next: ${nextDateString})`,
              color: tokens.colors.warning[400],
              isToday: false
            });
          } else {
            setEplScheduleInfo({
              text: 'No games scheduled',
              color: tokens.colors.neutral[500],
              isToday: false
            });
          }
        }
      } catch (error) {
        console.error('Error fetching EPL schedule:', error);
        setEplScheduleInfo({
          text: 'Schedule unavailable',
          color: tokens.colors.neutral[500],
          isToday: false
        });
      }
    };

    const fetchNBASchedule = async () => {
      try {
        const response = await fetch(`${APP_CONSTANTS.ODDS_API_BASE_URL}/sports/basketball_nba/odds/?apiKey=${APP_CONSTANTS.ODDS_API_KEY}&regions=us&markets=h2h`);
        const games = await response.json();
        
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const twoDaysFromNow = new Date(today.getTime() + 2 * 24 * 60 * 60 * 1000);
        
        // Check if there are any games in the next 2 days
        const gamesNext2Days = games.filter(game => {
          const gameTime = new Date(game.commence_time);
          return gameTime >= now && gameTime < twoDaysFromNow;
        });
        
        if (gamesNext2Days.length > 0) {
          const gamesToday = gamesNext2Days.filter(game => {
            const gameTime = new Date(game.commence_time);
            const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
            return gameTime >= today && gameTime < tomorrow;
          });
          
          if (gamesToday.length > 0) {
            setNbaScheduleInfo({
              text: `${gamesToday.length} games today`,
              color: tokens.colors.accent[400],
              isToday: true
            });
          } else {
            setNbaScheduleInfo({
              text: `${gamesNext2Days.length} games next 2 days`,
              color: tokens.colors.accent[400],
              isToday: true
            });
          }
        } else {
          // Get next 10 upcoming games
          const upcomingGames = games
            .filter(game => new Date(game.commence_time) >= now)
            .sort((a, b) => new Date(a.commence_time) - new Date(b.commence_time))
            .slice(0, 10);
          
          if (upcomingGames.length > 0) {
            const nextGameDate = new Date(upcomingGames[0].commence_time);
            const nextDateString = nextGameDate.toLocaleDateString('en-US', { 
              weekday: 'short', 
              month: 'short', 
              day: 'numeric' 
            });
            
            setNbaScheduleInfo({
              text: `${upcomingGames.length} upcoming games (next: ${nextDateString})`,
              color: tokens.colors.warning[400],
              isToday: false
            });
          } else {
            setNbaScheduleInfo({
              text: 'No games scheduled',
              color: tokens.colors.neutral[500],
              isToday: false
            });
          }
        }
      } catch (error) {
        console.error('Error fetching NBA schedule:', error);
        setNbaScheduleInfo({
          text: 'Schedule unavailable',
          color: tokens.colors.neutral[500],
          isToday: false
        });
      }
    };

    fetchMLBSchedule();
    fetchEPLSchedule();
    fetchNBASchedule();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: tokens.colors.gradients.dark,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: `3px solid ${tokens.colors.neutral[800]}`,
            borderTopColor: tokens.colors.accent[400],
            borderRadius: '50%',
            margin: '0 auto',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{ color: tokens.colors.neutral[400], marginTop: tokens.spacing[4] }}>
            Loading sports...
          </p>
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  const menuItems = [
    { label: 'Home', href: '/home' },
    { label: 'Sports', href: '/sports', active: true },
    { label: 'My Bets', href: '/my-bets' }
  ];

  // Available sports
  const availableSports = [
    {
      id: 'MLB',
      name: 'Major League Baseball',
      shortName: 'MLB',
      icon: '/logos/MLB/_MLB_logo.png',
      description: 'America\'s favorite pastime with comprehensive betting markets',
      gamesCount: mlbScheduleInfo.text,
      scheduleInfo: mlbScheduleInfo,
      popularMarkets: ['Moneyline', 'Run Line', 'Total Runs'],
      color: tokens.colors.primary[400],
      bgGradient: `linear-gradient(135deg, ${tokens.colors.primary[900]}33 0%, ${tokens.colors.primary[800]}22 100%)`,
      borderColor: tokens.colors.primary[700]
    },
    {
      id: 'soccer_epl',
      name: 'Premier League',
      shortName: 'EPL',
      icon: '/logos/Premier_League/_PremierLeague_logo.png',
      description: 'The world\'s most popular football league with exciting matches',
      gamesCount: eplScheduleInfo.text,
      scheduleInfo: eplScheduleInfo,
      popularMarkets: ['Moneyline', 'Over/Under', 'Both Teams Score'],
      color: tokens.colors.secondary[400],
      bgGradient: `linear-gradient(135deg, ${tokens.colors.secondary[900]}33 0%, ${tokens.colors.secondary[800]}22 100%)`,
      borderColor: tokens.colors.secondary[700]
    },
    {
      id: 'basketball_nba',
      name: 'National Basketball Association',
      shortName: 'NBA',
      icon: '/logos/NBA/_NBA_logo.png',
      description: 'Fast-paced basketball action with dynamic betting opportunities',
      gamesCount: nbaScheduleInfo.text,
      scheduleInfo: nbaScheduleInfo,
      popularMarkets: ['Moneyline', 'Point Spread', 'Total Points'],
      color: tokens.colors.accent[400],
      bgGradient: `linear-gradient(135deg, ${tokens.colors.accent[900]}33 0%, ${tokens.colors.accent[800]}22 100%)`,
      borderColor: tokens.colors.accent[700]
    }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: tokens.colors.gradients.dark,
      position: 'relative'
    }}>
      <Header
        logo="ELXR Sportsbook"
        user={userInfo ? {
          name: userInfo.given_name || userInfo.name,
          email: userInfo.email
        } : null}
        onLogout={handleLogout}
        menuItems={menuItems}
      />

      {/* Animated Background */}
      <div style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none'
      }}>
        <div style={{
          position: 'absolute',
          top: '15%',
          left: '10%',
          width: '500px',
          height: '500px',
          background: `radial-gradient(circle, ${tokens.colors.accent[900]}12 0%, transparent 70%)`,
          filter: 'blur(80px)',
          animation: 'float 30s ease-in-out infinite'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '15%',
          right: '5%',
          width: '600px',
          height: '600px',
          background: `radial-gradient(circle, ${tokens.colors.primary[900]}12 0%, transparent 70%)`,
          filter: 'blur(80px)',
          animation: 'float 35s ease-in-out infinite reverse'
        }} />
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          25% { transform: translate(20px, -20px) rotate(90deg); }
          50% { transform: translate(-10px, 10px) rotate(180deg); }
          75% { transform: translate(15px, -15px) rotate(270deg); }
        }
      `}</style>

      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: `${tokens.spacing[8]} ${tokens.spacing[6]}`,
        position: 'relative',
        zIndex: 1
      }}>
        {/* Page Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: tokens.spacing[12]
        }}>
          <h1 style={{
            fontSize: tokens.typography.fontSize['5xl'],
            fontWeight: tokens.typography.fontWeight.extrabold,
            background: tokens.colors.gradients.glow,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: tokens.spacing[6],
            filter: 'drop-shadow(0 0 40px rgba(34, 197, 94, 0.3))'
          }}>
            Choose Your Sport
          </h1>
          <p style={{
            color: tokens.colors.neutral[400],
            fontSize: tokens.typography.fontSize.xl,
            maxWidth: '700px',
            margin: '0 auto',
            lineHeight: 1.6
          }}>
            Select from our available sports and dive into the action with competitive odds and comprehensive betting markets
          </p>
        </div>

        {/* Sports Grid */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: tokens.spacing[8],
          marginBottom: tokens.spacing[12],
          maxWidth: '800px',
          margin: '0 auto',
          paddingBottom: tokens.spacing[4],
          paddingTop: tokens.spacing[4]
        }}>
          {availableSports.map((sport) => (
            <div
              key={sport.id}
              onClick={() => navigate(`/sports/${sport.id}`)}
              style={{
                background: `linear-gradient(135deg, ${tokens.colors.dark[800]}99 0%, ${tokens.colors.dark[900]}99 100%)`,
                backdropFilter: 'blur(20px)',
                border: `2px solid ${tokens.colors.neutral[800]}`,
                borderRadius: tokens.borderRadius['2xl'],
                padding: tokens.spacing[12],
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                width: '100%'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = sport.borderColor;
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = `0 20px 40px ${tokens.colors.dark[900]}88`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = tokens.colors.neutral[800];
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Background gradient overlay */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '50%',
                background: sport.bgGradient,
                opacity: 0.6
              }} />

              {/* Content */}
              <div style={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Header with icon and name */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: tokens.spacing[4],
                  marginBottom: tokens.spacing[2]
                }}>
                  <div style={{
                    background: sport.bgGradient,
                    borderRadius: tokens.borderRadius.xl,
                    padding: tokens.spacing[3],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '70px',
                    minHeight: '70px'
                  }}>
                    <img 
                      src={sport.icon} 
                      alt={sport.shortName}
                      style={{
                        width: '48px',
                        height: '48px',
                        objectFit: 'contain'
                      }}
                    />
                  </div>
                  <div>
                    <h2 style={{
                      color: tokens.colors.neutral[0],
                      fontSize: tokens.typography.fontSize['2xl'],
                      fontWeight: tokens.typography.fontWeight.bold,
                      marginBottom: tokens.spacing[1]
                    }}>
                      {sport.name}
                    </h2>
                    <p style={{
                      color: sport.color,
                      fontSize: tokens.typography.fontSize.lg,
                      fontWeight: tokens.typography.fontWeight.semibold
                    }}>
                      {sport.shortName}
                    </p>
                  </div>
                </div>

                {/* Description - Moved to upper half */}
                <p style={{
                  color: tokens.colors.neutral[400],
                  fontSize: tokens.typography.fontSize.base,
                  lineHeight: 1.6,
                  marginBottom: tokens.spacing[12],
                  marginTop: 0
                }}>
                  {sport.description}
                </p>

                {/* Features */}
                <div style={{ marginBottom: tokens.spacing[4] }}>
                  <p style={{
                    color: tokens.colors.neutral[500],
                    fontSize: tokens.typography.fontSize.sm,
                    fontWeight: tokens.typography.fontWeight.semibold,
                    marginBottom: tokens.spacing[2]
                  }}>
                    POPULAR MARKETS
                  </p>
                  <div style={{
                    display: 'flex',
                    gap: tokens.spacing[2],
                    flexWrap: 'wrap'
                  }}>
                    {sport.popularMarkets.map((market, index) => (
                      <span
                        key={index}
                        style={{
                          background: `${sport.color}22`,
                          color: sport.color,
                          padding: `${tokens.spacing[1]} ${tokens.spacing[3]}`,
                          borderRadius: tokens.borderRadius.full,
                          fontSize: tokens.typography.fontSize.xs,
                          fontWeight: tokens.typography.fontWeight.medium,
                          border: `1px solid ${sport.color}44`
                        }}
                      >
                        {market}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Action indicator */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: tokens.spacing[2]
                  }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: sport.scheduleInfo.color,
                      animation: (sport.scheduleInfo.color !== tokens.colors.neutral[500]) ? 'pulse 2s infinite' : 'none'
                    }} />
                    <span style={{
                      color: sport.scheduleInfo.color,
                      fontSize: tokens.typography.fontSize.sm,
                      fontWeight: tokens.typography.fontWeight.medium
                    }}>
                      {sport.gamesCount}
                    </span>
                  </div>
                  <div style={{
                    color: sport.color,
                    fontSize: '1.5rem',
                    transition: 'transform 0.2s'
                  }}>
                    →
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Coming Soon Section */}
        <div style={{
          background: `linear-gradient(135deg, ${tokens.colors.dark[800]}66 0%, ${tokens.colors.dark[900]}66 100%)`,
          backdropFilter: 'blur(10px)',
          border: `1px solid ${tokens.colors.neutral[800]}`,
          borderRadius: tokens.borderRadius['2xl'],
          padding: tokens.spacing[12],
          textAlign: 'center',
          maxWidth: '800px',
          margin: '0 auto',
          marginBottom: tokens.spacing[12]
        }}>
          <div style={{
            fontSize: '4rem',
            marginBottom: tokens.spacing[6],
            opacity: 0.3
          }}>
            🚀
          </div>
          <h3 style={{
            color: tokens.colors.neutral[300],
            fontSize: tokens.typography.fontSize['2xl'],
            fontWeight: tokens.typography.fontWeight.bold,
            marginBottom: tokens.spacing[4]
          }}>
            More Sports Coming Soon
          </h3>
          <p style={{
            color: tokens.colors.neutral[500],
            fontSize: tokens.typography.fontSize.base,
            maxWidth: '500px',
            margin: '0 auto',
            lineHeight: 1.6
          }}>
            We're constantly expanding our sports offerings. NFL, NHL, and many more sports will be available soon!
          </p>
        </div>

        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.3; }
          }
        `}</style>
      </div>
    </div>
  );
}

export default SportsPage; 